import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useTeam } from '../../../context/TeamContext';
import { useEquipment } from '../../../context/EquipmentContext';
import { useBed } from '../../../context/BedContext';
import { EQUIPMENT_TYPES, EQUIPMENT_STATUS, INSPECTION_ROUNDS } from '../../../data/nurseAppModels';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';

const InspectionReviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const { approveInspection, rejectInspection, equipment } = useEquipment();
  const { getBedById } = useBed();
  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [relatedInspections, setRelatedInspections] = useState([]);
  const [isLeader, setIsLeader] = useState(false);
  const [equipmentDataMap, setEquipmentDataMap] = useState({});
  
  // Helper function to get equipment image
  const getEquipmentImage = (equipmentId) => {
    if (!equipmentId) return null;
    
    // Try from equipmentDataMap first (loaded from Firestore)
    if (equipmentDataMap[equipmentId]) {
      const eq = equipmentDataMap[equipmentId];
      if (eq.images && Array.isArray(eq.images) && eq.images.length > 0) {
        const firstImage = typeof eq.images[0] === 'string' ? eq.images[0] : eq.images[0]?.url;
        if (firstImage) {
          console.log('Found image from equipmentDataMap:', equipmentId, firstImage);
          return firstImage;
        }
      }
      if (eq.thumbnail) {
        console.log('Found thumbnail from equipmentDataMap:', equipmentId, eq.thumbnail);
        return eq.thumbnail;
      }
    }
    
    // Fallback to equipment from context
    if (equipment && equipment.length > 0) {
      const eq = equipment.find(e => e.id === equipmentId);
      if (eq) {
        if (eq.images && Array.isArray(eq.images) && eq.images.length > 0) {
          const firstImage = typeof eq.images[0] === 'string' ? eq.images[0] : eq.images[0]?.url;
          if (firstImage) {
            console.log('Found image from equipment context:', equipmentId, firstImage);
            return firstImage;
          }
        }
        if (eq.thumbnail) {
          console.log('Found thumbnail from equipment context:', equipmentId, eq.thumbnail);
          return eq.thumbnail;
        }
      }
    }
    
    console.log('No image found for equipment:', equipmentId);
    return null;
  };

  useEffect(() => {
    const loadInspection = async () => {
      if (!id || !currentTeam) return;

      try {
        setLoading(true);
        const inspectionRef = doc(db, 'equipmentInspections', id);
        const inspectionSnap = await getDoc(inspectionRef);

        if (!inspectionSnap.exists()) {
          alert('ไม่พบรายการตรวจสอบ');
          navigate('/nurse/equipment/inspections/approval');
          return;
        }

        const data = inspectionSnap.data();
        setInspection({ id: inspectionSnap.id, ...data });

        // Load equipment data for images
        if (data.items && Array.isArray(data.items)) {
          const equipmentIds = [...new Set(data.items.map(item => item.equipmentId))];
          const equipmentMap = {};
          
          for (const equipmentId of equipmentIds) {
            try {
              const equipmentRef = doc(db, 'equipment', equipmentId);
              const equipmentSnap = await getDoc(equipmentRef);
              if (equipmentSnap.exists()) {
                equipmentMap[equipmentId] = { id: equipmentSnap.id, ...equipmentSnap.data() };
              }
            } catch (err) {
              console.error('Error loading equipment:', equipmentId, err);
            }
          }
          
          setEquipmentDataMap(equipmentMap);
          console.log('Loaded equipment data for images:', equipmentMap);
        }

        // Check if user is Leader, Deputy Leader, or the inspector (for viewing approved inspections)
        const userTeamRole = currentTeam.userRole;
        const userIsLeader = userTeamRole === 'LEADER' || userTeamRole === 'DEPUTY_LEADER';
        const isInspector = data.inspectedBy === currentUser?.uid;
        
        setIsLeader(userIsLeader);
        
        // Only allow non-leaders to view if they are the inspector and inspection is approved
        if (!userIsLeader && !(isInspector && data.status === 'APPROVED')) {
          alert('คุณไม่มีสิทธิ์เข้าถึงหน้านี้');
          navigate('/nurse/equipment');
          return;
        }

        // Load related inspections (same round and date)
        if (data.round && data.date) {
          const relatedQuery = query(
            collection(db, 'equipmentInspections'),
            where('teamId', '==', currentTeam.id),
            where('round', '==', data.round)
          );
          const relatedSnapshot = await getDocs(relatedQuery);
          const relatedData = relatedSnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(i => {
              const dateA = i.date?.toDate?.() || new Date(i.date);
              const dateB = data.date?.toDate?.() || new Date(data.date);
              return dateA.toDateString() === dateB.toDateString() && i.id !== id;
            });
          setRelatedInspections(relatedData);
        }
      } catch (err) {
        console.error('Error loading inspection:', err);
        alert(`เกิดข้อผิดพลาด: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadInspection();
  }, [id, currentTeam, navigate]);

  const getRoundLabel = (round) => {
    const rounds = {
      [INSPECTION_ROUNDS.MORNING_BEFORE]: 'เช้า - ก่อนเข้าเวร',
      [INSPECTION_ROUNDS.MORNING_AFTER]: 'เช้า - หลังเข้าเวร',
      [INSPECTION_ROUNDS.AFTERNOON_BEFORE]: 'บ่าย - ก่อนเข้าเวร',
      [INSPECTION_ROUNDS.AFTERNOON_AFTER]: 'บ่าย - หลังเข้าเวร',
      [INSPECTION_ROUNDS.NIGHT_BEFORE]: 'ดึก - ก่อนเข้าเวร',
      [INSPECTION_ROUNDS.NIGHT_AFTER]: 'ดึก - หลังเข้าเวร'
    };
    return rounds[round] || round;
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      [EQUIPMENT_STATUS.AVAILABLE]: 'พร้อมใช้',
      [EQUIPMENT_STATUS.IN_USE]: 'กำลังใช้งาน',
      [EQUIPMENT_STATUS.BORROWED]: 'ถูกยืม',
      [EQUIPMENT_STATUS.MAINTENANCE]: 'บำรุงรักษา',
      [EQUIPMENT_STATUS.REPAIR]: 'อยู่ระหว่างส่งซ่อม',
      [EQUIPMENT_STATUS.UNAVAILABLE]: 'ไม่พร้อมใช้งาน'
    };
    return statusLabels[status] || status;
  };

  const getStatusColor = (status) => {
    const statusColors = {
      [EQUIPMENT_STATUS.AVAILABLE]: 'bg-green-100 text-green-800',
      [EQUIPMENT_STATUS.IN_USE]: 'bg-blue-100 text-blue-800',
      [EQUIPMENT_STATUS.BORROWED]: 'bg-yellow-100 text-yellow-800',
      [EQUIPMENT_STATUS.MAINTENANCE]: 'bg-purple-100 text-purple-800',
      [EQUIPMENT_STATUS.REPAIR]: 'bg-red-100 text-red-800',
      [EQUIPMENT_STATUS.UNAVAILABLE]: 'bg-gray-100 text-gray-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getIssueTypeLabel = (issueType) => {
    const issueLabels = {
      'DAMAGED': 'เสียหาย',
      'NEEDS_REPAIR': 'ต้องซ่อม'
    };
    return issueLabels[issueType] || issueType;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleApprove = async () => {
    if (!window.confirm('ต้องการอนุมัติผลการตรวจสอบนี้หรือไม่?')) return;

    try {
      setIsProcessing(true);
      const result = await approveInspection(id);

      if (result.success) {
        alert('อนุมัติสำเร็จ');
        navigate('/nurse/equipment/inspections/approval');
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error approving:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('กรุณากรอกเหตุผลที่ไม่อนุมัติ');
      return;
    }

    try {
      setIsProcessing(true);
      const result = await rejectInspection(id, rejectionReason.trim());

      if (result.success) {
        alert('ปฏิเสธสำเร็จ');
        navigate('/nurse/equipment/inspections/approval');
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error rejecting:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">กำลังโหลดข้อมูล...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!inspection) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <h3 className="text-xl font-bold text-gray-800 mb-2">ไม่พบรายการตรวจสอบ</h3>
            <button
              onClick={() => navigate('/nurse/equipment/inspections/approval')}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              กลับ
            </button>
          </div>
        </div>
      </div>
    );
  }

  const smallItems = inspection.items?.filter(item => item.type === EQUIPMENT_TYPES.SMALL_ITEM) || [];
  const largeItems = inspection.items?.filter(item => item.type === EQUIPMENT_TYPES.LARGE_ITEM) || [];
  const isPending = inspection.status === 'PENDING_APPROVAL';

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/nurse/equipment/inspections/approval')}
            className="mb-4 text-blue-600 hover:text-blue-800 flex items-center"
          >
            ← กลับ
          </button>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">รายละเอียดการตรวจสอบ</h1>
              <p className="text-gray-600 mt-1">
                {getRoundLabel(inspection.round)} - {formatDate(inspection.date)}
              </p>
            </div>
            <span className={`px-3 py-1 rounded-lg text-sm font-semibold ${
              inspection.status === 'PENDING_APPROVAL' ? 'bg-yellow-100 text-yellow-800' :
              inspection.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
              inspection.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {inspection.status === 'PENDING_APPROVAL' ? '⏳ รออนุมัติ' :
               inspection.status === 'APPROVED' ? '✅ อนุมัติแล้ว' :
               inspection.status === 'REJECTED' ? '❌ ไม่อนุมัติ' : '📝 Draft'}
            </span>
          </div>
        </div>

        {/* Inspector Info */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">ข้อมูลผู้ตรวจสอบ</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">ผู้ตรวจสอบ</p>
              <p className="font-medium text-gray-800">{inspection.inspectedByName || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">วันที่ตรวจสอบ</p>
              <p className="font-medium text-gray-800">{formatDate(inspection.date)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">วันที่ส่ง</p>
              <p className="font-medium text-gray-800">{formatDate(inspection.submittedAt || inspection.createdAt)}</p>
            </div>
            {inspection.editedAfterSubmission && (
              <div>
                <p className="text-sm text-gray-600">แก้ไขเมื่อ</p>
                <p className="font-medium text-gray-800">{formatDate(inspection.lastEditedAt)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Related Inspections */}
        {relatedInspections.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-blue-800 mb-1">
                  มีการตรวจสอบอื่นในรอบเดียวกัน ({relatedInspections.length} รายการ)
                </p>
                <p className="text-sm text-blue-600">
                  สามารถเปรียบเทียบผลการตรวจสอบได้
                </p>
              </div>
              <Link
                to={`/nurse/equipment/inspections/compare?round=${inspection.round}&date=${inspection.date?.toDate?.()?.toISOString() || inspection.date}`}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                เปรียบเทียบผล
              </Link>
            </div>
          </div>
        )}

        {/* Small Items */}
        {smallItems.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">อุปกรณ์ชิ้นเล็ก</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700">ชื่อ</th>
                    <th className="text-center py-2 px-3 text-sm font-semibold text-gray-700">Stock ก่อนหน้า</th>
                    <th className="text-center py-2 px-3 text-sm font-semibold text-gray-700">คงเหลือ</th>
                    <th className="text-center py-2 px-3 text-sm font-semibold text-gray-700">เสียหาย</th>
                    <th className="text-center py-2 px-3 text-sm font-semibold text-gray-700">สูญหาย</th>
                  </tr>
                </thead>
                <tbody>
                  {smallItems.map((item, index) => {
                    const equipmentImage = getEquipmentImage(item.equipmentId);
                    return (
                    <tr key={index} className="border-b">
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-3">
                          {equipmentImage ? (
                            <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                              <img
                                src={equipmentImage}
                                alt={item.equipmentName}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.classList.add('bg-gray-200');
                                  e.target.parentElement.innerHTML = '<span class="text-gray-400 text-xl">🩺</span>';
                                }}
                              />
                            </div>
                          ) : (
                            <div className="w-12 h-12 flex-shrink-0 rounded-lg bg-gray-200 flex items-center justify-center">
                              <span className="text-gray-400 text-xl">🩺</span>
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-800">{item.equipmentName}</p>
                            <p className="text-xs text-gray-500">{item.equipmentCode}</p>
                          </div>
                        </div>
                      </td>
                      <td className="text-center py-2 px-3">{item.previousStock || 0}</td>
                      <td className="text-center py-2 px-3">{item.availableQuantity || 0}</td>
                      <td className="text-center py-2 px-3">
                        {item.damagedQuantity || 0}
                        {item.damagedReason && (
                          <div className="text-xs text-gray-500 mt-1">
                            {item.damagedReason === 'OTHER' ? item.damagedReasonOther : item.damagedReason}
                          </div>
                        )}
                      </td>
                      <td className="text-center py-2 px-3">
                        {item.lostQuantity || 0}
                        {item.lostReason && (
                          <div className="text-xs text-gray-500 mt-1">
                            {item.lostReason === 'OTHER' ? item.lostReasonOther : item.lostReason}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Large Items */}
        {largeItems.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">อุปกรณ์ชิ้นใหญ่</h2>
            <div className="space-y-3">
              {largeItems.map((item, index) => {
                const bed = item.bedId ? getBedById(item.bedId) : null;
                const equipmentImage = getEquipmentImage(item.equipmentId);
                return (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 flex items-start gap-3">
                        {equipmentImage ? (
                          <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                            <img
                              src={equipmentImage}
                              alt={item.equipmentName}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.classList.add('bg-gray-200');
                                e.target.parentElement.innerHTML = '<span class="text-gray-400 text-2xl">🩺</span>';
                              }}
                            />
                          </div>
                        ) : (
                          <div className="w-16 h-16 flex-shrink-0 rounded-lg bg-gray-200 flex items-center justify-center">
                            <span className="text-gray-400 text-2xl">🩺</span>
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-gray-800">{item.equipmentName}</p>
                          <p className="text-sm text-gray-500">{item.equipmentCode}</p>
                          {item.bedId && item.status === EQUIPMENT_STATUS.IN_USE && (
                            <p className="text-sm text-blue-600 mt-1 font-medium">
                              🛏️ ใช้งานอยู่ที่: {bed ? `${bed.bedName} (${bed.bedNumber}) - ${bed.ward}` : `เตียง ID: ${item.bedId}`}
                            </p>
                          )}
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-lg text-sm ${getStatusColor(item.status)}`}>
                        {getStatusLabel(item.status)}
                      </span>
                    </div>
                    {item.issueType && (
                      <div className="mt-2 text-sm text-gray-600">
                        <p>ปัญหา: {getIssueTypeLabel(item.issueType)}</p>
                        {item.issueDescription && <p className="text-xs text-gray-500 mt-1">{item.issueDescription}</p>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Notes */}
        {inspection.notes && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-2">หมายเหตุ</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{inspection.notes}</p>
          </div>
        )}

        {/* Actions - Only show for PENDING_APPROVAL status and if user is leader */}
        {inspection.status === 'PENDING_APPROVAL' && isLeader && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">การอนุมัติ</h2>
            <div className="flex gap-4">
              <button
                onClick={handleApprove}
                disabled={isProcessing}
                className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50"
              >
                {isProcessing ? 'กำลังอนุมัติ...' : '✅ อนุมัติ'}
              </button>
              <button
                onClick={() => setShowRejectModal(true)}
                disabled={isProcessing}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold disabled:opacity-50"
              >
                ❌ ไม่อนุมัติ
              </button>
            </div>
          </div>
        )}
        
        {/* View-only message for non-leaders viewing approved inspections */}
        {inspection.status === 'APPROVED' && !isLeader && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-blue-800 font-medium">
              ℹ️ รายการนี้ได้รับการอนุมัติแล้ว คุณสามารถดูรายละเอียดได้เท่านั้น
            </p>
          </div>
        )}

        {/* Reject Modal */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-bold text-gray-800 mb-4">ไม่อนุมัติผลการตรวจสอบ</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  เหตุผลที่ไม่อนุมัติ *
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="กรุณากรอกเหตุผลที่ไม่อนุมัติ..."
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleReject}
                  disabled={isProcessing || !rejectionReason.trim()}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50"
                >
                  {isProcessing ? 'กำลังส่ง...' : 'ยืนยัน'}
                </button>
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionReason('');
                  }}
                  disabled={isProcessing}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium disabled:opacity-50"
                >
                  ยกเลิก
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InspectionReviewPage;

